import { ComponentType } from '@angular/cdk/overlay';
import { ComponentRef, EmbeddedViewRef, Injectable, ViewContainerRef } from '@angular/core';

export type HtmlDataURI = `data:${typeof HTML_FILE_TYPE},${string}`;

export const HTML_FILE_TYPE = 'text/html;charset=utf-8' as const;

/**
 * Service to support exporting an angular component to HTML
 */
@Injectable({ providedIn: 'root' })
export class HtmlExportService {
    /**
     * Get report as data url
     * @param componentClass Class of the component to convert
     * @param inputs Input data to pass to component inputs
     * @param viewContainerRef ViewRef of the calling component to add context to this one
     * @param componentModifier Callback function to modify component data after setting inputs
     * @param waitFor Callback function to wait till the component is full rendered
     */
    async getReportHtmlDataUrl<TComponent>(
        componentClass: ComponentType<TComponent>,
        viewContainerRef: ViewContainerRef,
        inputs: Partial<ComponentType<TComponent>> = {},
        componentModifier?: (componentRef: ComponentRef<TComponent>) => Promise<void>,
        waitFor?: (componentRef: ComponentRef<TComponent>) => Promise<void>
    ): Promise<HtmlDataURI> {
        const htmlString = await this.getReportHTML<TComponent>(componentClass, viewContainerRef, inputs, componentModifier, waitFor);
        return `data:${HTML_FILE_TYPE},${encodeURIComponent(htmlString)}`;
    }

    /**
     * Get report as an HTML string
     * @param componentClass Class of the component to convert
     * @param inputs Input data to pass to component inputs
     * @param viewContainerRef ViewRef of the calling component to add context to this one
     * @param componentModifier Callback function to modify component data after setting inputs
     * @param waitFor Callback function to wait till the component is full rendered
     */
    async getReportHTML<TComponent>(
        componentClass: ComponentType<TComponent>,
        viewContainerRef: ViewContainerRef,
        inputs: Partial<ComponentType<TComponent>> = {},
        componentModifier?: (componentRef: ComponentRef<TComponent>) => Promise<void>,
        waitFor?: (componentRef: ComponentRef<TComponent>) => Promise<void>
    ): Promise<string> {
        const componentRef: ComponentRef<TComponent> = viewContainerRef.createComponent(componentClass);

        Object.entries(inputs).forEach(([key, value]) => {
            componentRef.instance[key as keyof TComponent] = value as any;
        });

        if (componentModifier) {
            await componentModifier(componentRef);
        }

        componentRef.changeDetectorRef.detectChanges();

        if (waitFor) {
            await waitFor(componentRef);
        }

        const domElem = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
        const styles = this.getUsedCssRules(domElem);

        const html = `
            <html>
            <head>
                <style>
                    ${styles}
                </style>
                <style>
                    /* force print backgrounds */
                    @media print {
                        * {
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                        }
                        body {
                            background-color: white !important;
                        }
                    }
                </style>
            </head>
            <body>
                ${domElem.outerHTML}
            </body>
            </html>
            `;

        componentRef.destroy();

        const htmlMini = html
            .replace(/\s+/g, ' ')
            .replace(/> </g, '><')
            .replace(/<!--.*?-->/g, '');

        return htmlMini;
    }

    private getAllCssRules(): CSSRule[] {
        const sheets = [...document.styleSheets] as CSSStyleSheet[];
        return sheets.flatMap((sheet) => {
            try {
                return [...sheet.cssRules] as CSSRule[];
            } catch (e) {
                return [];
            }
        });
    }

    /**
     * Gets top global styles and the style rules that apply only to a given html element and descendants.
     */
    private getUsedCssRules(element: HTMLElement): string {
        const allCssRules = this.getAllCssRules();
        const usedCssRules: string[] = [];
        const topSelectors = [':root', 'body', 'html'];

        const checkElement = (element: HTMLElement): void => {
            const applicableRules = allCssRules
                .filter((rule) => rule instanceof CSSStyleRule)
                .filter((rule) => {
                    const selector = (rule as CSSStyleRule).selectorText;
                    const isTopRule = topSelectors.some((topSelector) => selector.includes(topSelector));
                    return isTopRule || element.matches(selector);
                })
                .map((rule) => rule.cssText);

            usedCssRules.push(...applicableRules);

            [...element.children].forEach((child) => checkElement(child as HTMLElement));
        };

        checkElement(element);

        return [...new Set(usedCssRules)].join('\n');
    }
}
